import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RiderAccessModal } from './RiderAccessModal';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, backendUser, logout } = useAuth();
  const [showRiderModal, setShowRiderModal] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const hasLoggedOutRef = useRef(false);

  // Check for regular riders when user and backendUser are loaded
  useEffect(() => {
    if (!user || !backendUser || hasLoggedOutRef.current) return;
    
    const allowedRoles = ['SYSTEM_ADMIN', 'SUPERADMIN', 'STORE_ADMIN', 'STORE_MANAGER', 'ADMIN'];
    const userRole = backendUser.role || 'USER';
    if (!allowedRoles.includes(userRole)) {
      hasLoggedOutRef.current = true;
      setShowRiderModal(true);
    }
  }, [user, backendUser]);

  // Handle modal confirmation - navigate to rider login (modal handles navigation)
  const handleRiderModalConfirm = async () => {
    // Modal will navigate to rider login page
    // Logout here to clear admin panel session
    setShowRiderModal(false);
    await logout().catch(console.error);
    setShouldRedirect(true);
  };

  if (!user) {
    return <Navigate to="/store" replace />;
  }

  // Redirect after logout
  if (shouldRedirect) {
    return <Navigate to="/store" replace />;
  }

  // Allow access only for admin roles - regular users (USER role) must use the mobile app
  // SUPERADMIN can access both admin panel and mobile app
  const allowedRoles = ['SYSTEM_ADMIN', 'SUPERADMIN', 'STORE_ADMIN', 'STORE_MANAGER', 'ADMIN'];
  const userRole = backendUser?.role || 'USER';
  if (!allowedRoles.includes(userRole)) {
    // USER role should use the mobile app, not the admin panel
    // Show modal and block access until user acknowledges
    return (
      <>
        <RiderAccessModal 
          isOpen={showRiderModal} 
          onConfirm={handleRiderModalConfirm} 
        />
        <div className="min-h-screen bg-gray-50" />
      </>
    );
  }

  // Redirect to password reset if required
  if (backendUser?.passwordResetRequired) {
    return <Navigate to="/reset-password" replace />;
  }

  return (
    <>
      <RiderAccessModal 
        isOpen={showRiderModal} 
        onConfirm={handleRiderModalConfirm} 
      />
      {children}
    </>
  );
};